// src/components/ProgressIndicator.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';

const STAGES = [
  { id: 1, label: 'Parsing document', duration: 1000 },
  { id: 2, label: 'Extracting text', duration: 800 },
  { id: 3, label: 'Analyzing keywords', duration: 1200 },
  { id: 4, label: 'Generating AI insights', duration: 1500 },
  { id: 5, label: 'Finalizing results', duration: 500 }
];

export default function ProgressIndicator({ isActive }) {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setCurrentStage(0);
      setProgress(0);
      return;
    }

    let stageIndex = 0;
    let progressInterval;

    const advanceStage = () => {
      if (stageIndex < STAGES.length) {
        setCurrentStage(stageIndex + 1);
        
        // Animate progress for this stage
        const stage = STAGES[stageIndex];
        const startProgress = (stageIndex / STAGES.length) * 100;
        const endProgress = ((stageIndex + 1) / STAGES.length) * 100;
        const steps = 20;
        const increment = (endProgress - startProgress) / steps;
        let currentProgress = startProgress;

        progressInterval = setInterval(() => {
          currentProgress += increment;
          if (currentProgress >= endProgress) {
            currentProgress = endProgress;
            clearInterval(progressInterval);
          }
          setProgress(Math.min(currentProgress, 100));
        }, stage.duration / steps);

        stageIndex++;
        if (stageIndex < STAGES.length) {
          setTimeout(advanceStage, stage.duration);
        }
      }
    };

    advanceStage();

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="w-full max-w-2xl mx-auto mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Analyzing Your Resume
          </h3>
          <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
            {Math.round(progress)}%
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stages list */}
      <div className="space-y-2">
        {STAGES.map((stage) => (
          <div
            key={stage.id}
            className={`flex items-center gap-3 text-sm transition-all duration-300 ${
              currentStage >= stage.id
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-gray-400 dark:text-gray-600'
            }`}
          >
            {currentStage > stage.id ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : currentStage === stage.id ? (
              <Loader2 className="w-4 h-4 text-cyan-500 animate-spin flex-shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
            )}
            <span className={currentStage === stage.id ? 'font-medium' : ''}>
              {stage.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}